/**
 * Small graph parts every voice is built out of.
 *
 * Nothing here has an opinion about aeroplanes. It exists so that `pistonEngine.ts` reads as a
 * description of an engine rather than as forty lines of `createBiquadFilter` boilerplate, and so
 * that the decisions that *are* shared — how a noise loop is started, how a one-shot is torn
 * down, what a "bandpass" means here — are made once.
 *
 * ## The allocation position, stated once for the whole audio system
 *
 * Non-negotiable #3 forbids heap allocation in per-frame hot paths. Every *continuous* voice in
 * this system is built at construction and afterwards only has `AudioParam`s written to it, so
 * the frame path allocates nothing.
 *
 * One-shots are different, and the difference is in the Web Audio specification rather than in
 * this design: `AudioBufferSourceNode` and `OscillatorNode` are single-use by definition, so a
 * sound that fires cannot avoid constructing nodes. That is accepted, because a one-shot is an
 * *event* — a wheel touching, a switch moving, a round leaving a barrel — and not a per-frame
 * path. {@link OneShotPool} bounds the damage: it caps how many can be live at once, so a stuck
 * trigger cannot turn into unbounded allocation, and it disconnects each node the moment it ends
 * so nothing is retained.
 *
 * `updateFrame(alpha)`: the parts themselves have no update. Their owners write their parameters.
 */

import type { Rng } from '../../core/rng.js';
import { percussive, setImmediate } from '../param.js';

/**
 * A looping noise source, started immediately.
 *
 * `start()` on a suspended context is legal and does the right thing: playback begins when the
 * context is resumed, so the whole graph can be built before the user's first gesture and there
 * is nothing to kick off afterwards.
 *
 * `loopEndS` matters for the pink buffer, whose usable length is shorter than its allocated
 * length because the tail has been folded back over the head to hide the seam (see `noise.ts`).
 */
export function loopingSource(
  context: AudioContext,
  buffer: AudioBuffer,
  loopEndS: number,
  playbackRate = 1,
  startOffsetS = 0,
): AudioBufferSourceNode {
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.loop = true;
  source.loopStart = 0;
  source.loopEnd = Math.min(loopEndS, buffer.duration);
  source.playbackRate.value = playbackRate;
  source.start(0, startOffsetS % Math.max(0.001, source.loopEnd));
  return source;
}

/** A bandpass. `q` of 1 is a broad colour; 8 is a resonance you can hear the pitch of. */
export function bandpass(context: AudioContext, hz: number, q = 1): BiquadFilterNode {
  const filter = context.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = hz;
  filter.Q.value = q;
  return filter;
}

/** A lowpass at the Butterworth Q, which is the one that does not ring. */
export function lowpass(context: AudioContext, hz: number, q = 0.7071): BiquadFilterNode {
  const filter = context.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = hz;
  filter.Q.value = q;
  return filter;
}

export function highpass(context: AudioContext, hz: number, q = 0.7071): BiquadFilterNode {
  const filter = context.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = hz;
  filter.Q.value = q;
  return filter;
}

/** A gain node with a starting value, for readability at the call site. */
export function gainNode(context: AudioContext, value = 0): GainNode {
  const node = context.createGain();
  node.gain.value = value;
  return node;
}

/**
 * A `ConstantSourceNode` — a DC signal, used here to drive other nodes' parameters.
 *
 * This is how the engine's eight harmonics stay in tune with each other: rather than writing
 * eight frequencies a frame and hoping they agree, one constant source carries the fundamental
 * and is fanned out through fixed gains into each oscillator's `frequency`. One write moves all
 * eight, sample-accurately, with no possibility of drift or beating between them.
 *
 * Falls back to null on an implementation without it; callers then write frequencies directly.
 */
export function constantSource(context: AudioContext, value: number): ConstantSourceNode | null {
  const create = (context as { createConstantSource?: () => ConstantSourceNode }).createConstantSource;
  if (typeof create !== 'function') return null;
  const node = create.call(context);
  node.offset.value = value;
  node.start(0);
  return node;
}

/** A wave shaper with a prebuilt curve. */
export function waveShaper(context: AudioContext, curve: Float32Array<ArrayBuffer>): WaveShaperNode {
  const shaper = context.createWaveShaper();
  shaper.curve = curve;
  return shaper;
}

/**
 * Fires baked one-shot buffers, with a ceiling on how many can be alive at once.
 *
 * The ceiling is not about CPU — a few dozen buffer sources is nothing — it is about the
 * allocation guarantee above. A bug that fires a click every frame should get quieter and stop,
 * not allocate two nodes per frame forever.
 */
export class OneShotPool {
  private readonly context: AudioContext;
  private readonly destination: AudioNode;
  private readonly limit: number;
  /**
   * The seeded RNG, not `Math.random`.
   *
   * Non-negotiable #7: *all* randomness goes through one seeded generator. It is tempting to
   * treat a few per cent of pitch jitter on a gunshot as beneath the rule — it is not. Two
   * multiplayer clients whose guns are pitched differently is a desynchronisation nobody would
   * ever find, and a replay that sounds different every time it is played back is not a replay.
   */
  private readonly rng: Rng;
  private live = 0;

  constructor(context: AudioContext, destination: AudioNode, rng: Rng, limit = 24) {
    this.context = context;
    this.destination = destination;
    this.rng = rng;
    this.limit = limit;
  }

  get liveCount(): number {
    return this.live;
  }

  /**
   * Play `buffer` once.
   *
   * @param gain          Linear amplitude. Anything at or below zero is silently dropped, which
   *                      is what makes "thump scaled by sink rate" free of a special case for a
   *                      landing so gentle it made no sound.
   * @param playbackRate  Resamples the buffer: pitch and duration together, as a real object's
   *                      size changes both. Also where Doppler lands for one-shots.
   * @param detuneRandom  Random ± fraction on the rate, so a repeated sound is never identical.
   *                      A gun firing thirty identical rounds is the most artificial sound there
   *                      is; a few per cent of variation removes the machine-gun-of-one-sample
   *                      effect entirely.
   */
  play(buffer: AudioBuffer, gain: number, playbackRate = 1, detuneRandom = 0): void {
    if (!(gain > 0) || this.live >= this.limit) return;
    const context = this.context;
    const now = context.currentTime;

    const source = context.createBufferSource();
    source.buffer = buffer;
    const rate = playbackRate * (1 + this.rng.signed() * detuneRandom);
    source.playbackRate.value = Math.max(0.05, rate);

    const amp = context.createGain();
    setImmediate(amp.gain, gain, now);
    source.connect(amp);
    amp.connect(this.destination);

    this.live++;
    source.onended = (): void => {
      this.live--;
      source.disconnect();
      amp.disconnect();
      source.onended = null;
    };
    source.start(now);
  }

  /**
   * Play `buffer` with an explicit percussive envelope on top of its own.
   *
   * For the cases where the baked decay is the *body* of the sound but the level has to be shaped
   * by something the bake could not know — a cannon report that has to duck under a burst, an
   * explosion whose tail is cut short by distance.
   */
  playShaped(
    buffer: AudioBuffer,
    peak: number,
    attackS: number,
    decayS: number,
    playbackRate = 1,
  ): void {
    if (!(peak > 0) || this.live >= this.limit) return;
    const context = this.context;
    const now = context.currentTime;

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = Math.max(0.05, playbackRate);

    const amp = context.createGain();
    amp.gain.value = 0;
    percussive(amp.gain, peak, attackS, decayS, now);
    source.connect(amp);
    amp.connect(this.destination);

    this.live++;
    source.onended = (): void => {
      this.live--;
      source.disconnect();
      amp.disconnect();
      source.onended = null;
    };
    source.start(now);
  }
}

/** Disconnect a list of nodes, ignoring anything already gone. */
export function disconnectAll(nodes: readonly (AudioNode | null | undefined)[]): void {
  for (const node of nodes) {
    try {
      node?.disconnect();
    } catch {
      /* already disconnected */
    }
  }
}
